import { pipe, S } from "@mobily/ts-belt";
import PropTypes from "prop-types";
import * as React from "react";
import { Money, Product, SignType } from "../../domain";
import { useLivestreamWarningContext } from "../../pages/Dashboard/ShopEditor";
import AffirmIcons from "../Checkout/Form/AffirmIcons";
import { WarningIcon } from "../../themeV2";
import ProductThumbnail from "../ProductThumbnail/ProductThumbnail";
import SignTypeChip from "../SignTypeChip/SignTypeChip";
import styles from "./ProductImage.module.css";

function shouldShowAffirm(product) {
  return (
    product &&
    Money.add(product.price, product.productBump).gt(
      Money.for({ value: 500, currency: "usd" }),
    )
  );
}

const ProductImage = React.memo(function ProductImage({
  product,
  isStorefront = false,
  isSmall,
  inset,
}) {
  const livestreamWarning = useLivestreamWarningContext();
  const shouldShowWarning =
    livestreamWarning && product.signedType === SignType.livestream;

  const isVaulted = Boolean(product.vaultedUntil);

  return (
    <ProductThumbnail
      src={product?.imageUrl}
      alt={product?.name}
      inset={inset ?? product.shouldContainImage}
      className={styles.container}
      title={
        !isStorefront && shouldShowWarning
          ? "This product will not be available to customers until an event date has been set"
          : undefined
      }
    >
      {isVaulted && (
        <div className={styles.vaultedOverlay}>
          <span>VAULTED</span>
        </div>
      )}

      {product.isSoldOut && !isVaulted && (
        <div className={styles.soldOutOverlay}>
          <span>SOLD OUT</span>
        </div>
      )}

      {!isStorefront && shouldShowWarning && (
        <div className={styles.topLeft}>
          <WarningIcon
            style={{
              color: "var(--md-error)",
              width: 30,
              height: 30,
            }}
          />
        </div>
      )}

      <div className={styles.topRight}>
        <SignTypeChip
          signedType={product.signedType}
          label={
            isSmall
              ? pipe(
                  SignType.toLabel(product.signedType),
                  S.trim,
                  S.replaceAll("(Ships Fast)", ""),
                )
              : SignType.toLabel(product.signedType)
          }
        />
      </div>

      {isStorefront && shouldShowAffirm(product) && (
        <div className={styles.affirmBox}>
          <AffirmIcons shadow size="small" />
        </div>
      )}
    </ProductThumbnail>
  );
});

ProductImage.propTypes = {
  product: PropTypes.oneOfType([
    PropTypes.instanceOf(Product),
    PropTypes.shape({
      aspectRatio: PropTypes.number.isRequired,
      imageUrl: PropTypes.string.isRequired,
      isSoldOut: PropTypes.bool,
      name: PropTypes.string.isRequired,
      price: PropTypes.object,
      productBump: PropTypes.object,
      productId: PropTypes.number,
      shouldContainImage: PropTypes.bool.isRequired,
      signedType: PropTypes.string.isRequired,
      vaultedUntil: PropTypes.string,
    }),
  ]),
  inset: PropTypes.bool,
  isStorefront: PropTypes.bool,
  isSmall: PropTypes.bool,
};

export default ProductImage;
